import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useDates } from "../../../dates/DatesContext";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import Loading from "../../../components/core/blocks/Loading";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import getMetricSpec from "../../merchandising/utils/metrics";
import { useDSPProductDatasetsQuery } from "../hooks/useDSPProductDatasetsQuery";
import ExternalProductLink from "../../../components/custom/merchandise/ExternalProductLink";

const DSPProductExpander = ({ row, lineItemId }) => {
  const { group } = useDates();
  const { asin, product_name } = row.original ?? {};

  const { data: datasetsResult, isLoading: isProductDataLoading } = useDSPProductDatasetsQuery({
    dimensions: ["asin"],
    lineItemId,
    asin,
  });

  const { data: datasetsTimeSeriesResult, isLoading: isProductTimeSeriesDataLoading } = useDSPProductDatasetsQuery({
    dimensions: ["asin", group],
    lineItemId,
    asin,
  });

  const data = useMemo(() => {
    const timeSeriesRows = datasetsTimeSeriesResult?.data ?? [];
    return {
      date: timeSeriesRows.map((row) => ({ ...row, start_date: row.start_date ?? row.date })),
      total: datasetsResult?.data?.[0] ?? {},
      metadata: {},
      isLoading: isProductDataLoading,
    };
  }, [datasetsResult, datasetsTimeSeriesResult, isProductDataLoading]);

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["dsp_total_detail_page_views", "dsp_total_add_to_cart", "dsp_total_purchases"],
    "dspProductExpander",
  );

  const metrics = useMemo(
    () => [
      getMetricSpec("dsp_total_detail_page_views"),
      getMetricSpec("dsp_new_to_brand_detail_page_views"),
      getMetricSpec("dsp_total_add_to_cart"),
      getMetricSpec("dsp_total_purchases"),
      getMetricSpec("dsp_total_new_to_brand_dpvs"),
      getMetricSpec("dsp_total_units"),
    ],
    [],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isProductDataLoading || isProductTimeSeriesDataLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <div className="bg-white">
      <Row className="pt-3">
        <Col xs={12} className="pb-4 px-5">
          <h3
            style={{
              fontSize: 22,
            }}
          >
            {product_name}
          </h3>
          <span className="fs-standard">
            {asin} <ExternalProductLink asin={asin} />
          </span>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="px-5 py-3">
          <>
            <Col xs={12}>
              <MultiMetricChart
                metrics={metrics}
                selectedMetrics={selectedMetrics}
                data={data?.date ?? []}
                group={group}
                handleLegendChange={handleLegendChange}
                showAnnotations={false}
              />
            </Col>
            <Col xs={12}>
              <Row className="pt-4">
                {metrics?.map((m, i) => {
                  return (
                    <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                      <div className="">
                        <div>
                          <DetailedMetric center metric={m} data={data?.total} showTarget={false} />
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Col>
          </>
        </Col>
      </Row>
    </div>
  );
};

export default DSPProductExpander;
